import React, { useState } from "react";
import { TitleBlock, Field, Input, Note, Actions, PrimaryBtn, SecondaryBtn } from "../../../../auth/v2/pieces";
import { OptionCard, PlanCard, Segmented, Section, RowList, CheckCircle, fmtMoney, fmtRange } from "../onboardingPieces";
import { usePhoneTheme } from "../../../../ForBrands/useIsPhone";
import { previewCoachCode, linkCoachCode, fetchEntitlementNow } from "../onboardingV2Services";
import { codeTeamKey, codeTeamLinkArgs, codeTeamMeta, defaultCodeTeam, pickedCodeTeam } from "../../../../auth/Onboarding/codeTeams";
import { coverageOf, coverageSentence } from "../../../teamCoverage";
import { displayTeamName } from "../../../../../utils/teamNames";

// On phones (the AuthFrame phone theme, below 640px) these steps follow
// ATHLETE MODE V2 (mobile): the promo field has no label and its error sits
// under it as its own block (15 1573:508, 15a 1579:1065), the code-applied
// card carries the 64px check (16 1576:766), and the rate card tiles are
// square (VitalCell 1765:1520). The plan step needs nothing of its own: its
// billing segments already share the row on phones, and the price stays the
// web's (D-o12). 640px and up is unchanged.

const PRICE = { monthly: "$4.99/mo", annual: "$49.99/yr" };

// 14 · Plan and payment (14a payment declined). Stripe Checkout shows card
// declines on its own page; coming back from it without paying is a cancel,
// which shows the plain plan with a neutral note.
export const PlanStep = ({ declined = false, cancelled = false, onSubscribe, onCode, onKeepCore }) => {
  const [plan, setPlan] = useState("premium");
  const [interval, setInterval] = useState("monthly");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (plan === "basic") { onKeepCore(); return; }
    setBusy(true); setError("");
    try { await onSubscribe(interval); } catch (e) { setError(e.message || "We could not start checkout."); setBusy(false); }
  };
  return (
    <>
      {declined ? (
        <TitleBlock tone="critical" eyebrow="Payment declined" title="We could not process that card" sub="Nothing was charged. Your Core metrics are untouched and still free." />
      ) : (
        <TitleBlock eyebrow="Paywall" title="Unlock Premium metrics" sub="Your Core score stays free forever. Optimized adds what brands actually price you on." />
      )}
      {declined && <Note tone="critical">Your bank declined the charge. Try another card, or use a code if your school gave you one.</Note>}
      {!declined && cancelled && <Note>Checkout cancelled. Nothing was charged.</Note>}
      <div className="flex flex-col gap-[10px]">
        <PlanCard selected={plan === "basic"} onClick={() => setPlan("basic")} title="Basic" sub="Visibility score · follower and engagement data" price="Free" />
        <PlanCard selected={plan === "premium"} onClick={() => setPlan("premium")} title="Premium" sub="Position rank · brand fit · NILx value · Atlas access" price={PRICE[interval]} />
      </div>
      {plan === "premium" && (
        <Field label="Billing" hint="Annual is two months free.">
          <Segmented value={interval} onChange={setInterval} options={[["monthly", "Monthly · $4.99"], ["annual", "Annual · $49.99"]]} />
        </Field>
      )}
      <Field label="Card">
        <Input readOnly placeholder="Entered securely on the next screen" aria-label="Card" onClick={go} />
      </Field>
      <div className="flex items-center justify-between gap-3 rounded-[8px] bg-bm-subtle px-4 py-[14px]">
        <span className="bm-body-m text-bm-muted">{declined ? "Got a code? Enter it instead and skip payment entirely." : "Got a code late? Enter it here to skip payment."}</span>
        <button type="button" onClick={onCode} className="shrink-0 bm-label-l text-bm-orange hover:underline">Add code</button>
      </div>
      {error && <Note tone="critical">{error}</Note>}
      <Actions>
        <PrimaryBtn onClick={go} loading={busy}>{plan === "basic" ? "Keep Core for now" : declined ? "Try another card" : "Start subscription"}</PrimaryBtn>
        <SecondaryBtn onClick={onKeepCore} disabled={busy}>Keep Core for now</SecondaryBtn>
      </Actions>
    </>
  );
};

// The promo screen's answer for a team or coach code (409 team_code): the
// wizard's services throw { status, data }, axios { response }.
export const isTeamCodeAnswer = (e) => {
  const status = e?.status ?? e?.response?.status;
  const error = e?.data?.error ?? e?.response?.data?.error;
  return status === 409 && error === "team_code";
};

// What the athlete reads after joining with a team code typed on the promo
// screen: the team, and whether the team includes Premium (the entitlement
// read again after the link). When that read failed nothing is said about
// the fee: the next step reads it again.
export const joinedSummary = (team, entitlement) => {
  const coverage = coverageOf(entitlement);
  const premium = entitlement?.entitlement === "athlete_premium";
  let sub = "Your profile is verified on the team. Premium is not included with it, so the fee still applies.";
  if (!entitlement?.entitlement) sub = "Your profile is verified on the team.";
  else if (premium) sub = coverage ? `${coverageSentence(coverage)} No payment needed.` : "Premium is unlocked. No payment needed.";
  return { teamName: team || "your team", premium, coverage, sub };
};

// A refused link in the server's words (a 4xx: "Invalid or unverified
// code.", a team cap sentence), never a 5xx body's text.
export const linkRefusalOf = (err) => {
  const status = err?.response?.status ?? err?.status;
  const data = err?.response?.data ?? err?.data;
  const words = status && status >= 500 ? "" : String(data?.message || data?.error || "").trim();
  return words || "We could not link this code.";
};

// A team code typed on the promo screen: "Join <team> with this code" (the
// code's preview, a squad to pick when it covers several), then the link
// and a fresh read of the entitlement, since a team plan or a CACHE team
// code can include Premium (notes 10/19). `onJoined(summary)` lets the
// wizard move on; without it Continue skips to the plan.
const JoinWithCode = ({ offer, onBack, onSkip, onJoined }) => {
  const [pick, setPick] = useState(() => { const first = defaultCodeTeam(offer.preview.teams || []); return first ? codeTeamKey(first) : ""; });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [joined, setJoined] = useState(null);
  const teams = offer.preview.teams || [];
  const chosen = pickedCodeTeam(teams, pick);
  const needsPick = teams.length > 1 && !chosen;
  const program = offer.preview.programName || (teams.length > 1 ? teams[0]?.teamName : "") || "";
  const label = chosen ? displayTeamName(chosen) || chosen.teamName : program || "your team";

  const join = async () => {
    if (!chosen || busy) return;
    setBusy(true); setError("");
    try {
      const linked = await linkCoachCode(offer.code, ...codeTeamLinkArgs(chosen));
      let entitlement = null;
      try { entitlement = await fetchEntitlementNow(); } catch { entitlement = null; }
      setJoined(joinedSummary(displayTeamName(chosen) || linked?.teamName || chosen.teamName, entitlement));
    } catch (err) {
      setError(linkRefusalOf(err));
    } finally { setBusy(false); }
  };

  if (joined) {
    return (
      <>
        <TitleBlock eyebrow={`Code ${offer.code}`} title={`You joined ${joined.teamName}`} sub={joined.sub} />
        <Actions><PrimaryBtn onClick={() => (onJoined ? onJoined(joined) : onSkip())}>Continue</PrimaryBtn></Actions>
      </>
    );
  }
  return (
    <>
      <TitleBlock eyebrow={`Code ${offer.code}`} title={`Join ${teams.length > 1 ? program || "your team" : label} with this code`} sub="That's a team code, not a promo code. Joining links your profile to the team and verifies it." />
      {teams.length > 1 ? (
        <div role="radiogroup" aria-label="Pick your team" className="flex flex-col gap-[10px]">
          {teams.map((t) => {
            const key = codeTeamKey(t);
            return (
              <OptionCard key={key} role="radio" aria-checked={pick === key} disabled={!!t.alreadyLinked || busy} selected={pick === key}
                onClick={() => !t.alreadyLinked && setPick(key)} title={displayTeamName(t) || t.teamName}
                sub={codeTeamMeta(t) + (t.alreadyLinked ? " · already linked" : "")} />
            );
          })}
        </div>
      ) : (
        <div className="rounded-[12px] border border-bm-border bg-white p-4">
          <div className="bm-h-s text-bm-ink">{label}</div>
          {codeTeamMeta(chosen) && <div className="bm-body-m text-bm-muted">{codeTeamMeta(chosen)}</div>}
        </div>
      )}
      {offer.preview.problem ? <Note tone="critical">{offer.preview.problem}</Note>
        : chosen?.alreadyLinked ? <Note>You are already verified on this team.</Note>
        : needsPick ? <Note>{offer.preview.scope === "program" && program ? `This code covers every age group of ${program}. Pick yours.` : "Pick your team to continue."}</Note>
        : offer.preview.wouldAddTeam ? <Note>Your profile keeps {offer.preview.yourTeam || "your current team"} as your team. {label} is added as another roster you play on.</Note>
        : null}
      {error && <Note tone="critical">{error}</Note>}
      <Actions>
        <PrimaryBtn onClick={join} loading={busy} disabled={!!offer.preview.problem || needsPick || !!chosen?.alreadyLinked}>{needsPick ? "Join the team" : `Join ${label}`}</PrimaryBtn>
        <SecondaryBtn onClick={onBack} disabled={busy}>Use a different code</SecondaryBtn>
      </Actions>
    </>
  );
};

// 15 · Promo code (15a invalid). A team code typed here (409 team_code) is
// offered as "Join <team> with this code" (JoinWithCode).
export const PromoStep = ({ onApply, onSkip, onJoined }) => {
  const phone = usePhoneTheme();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState(null);
  // The code's preview, or null when it cannot be read.
  const previewOf = async (typed) => {
    try {
      const preview = await previewCoachCode(typed);
      return preview?.teams?.length ? { code: preview.code || typed, preview } : null;
    } catch { return null; }
  };
  const apply = async () => {
    if (!code.trim()) { setError("Enter a code."); return; }
    setBusy(true); setError("");
    try { await onApply(code.trim()); } catch (e) {
      const joinable = isTeamCodeAnswer(e) ? await previewOf(code.trim().toUpperCase()) : null;
      if (joinable) setOffer(joinable);
      else setError(e.message || "That code is expired or not recognised. Check with whoever gave it to you.");
    } finally { setBusy(false); }
  };
  if (offer) return <JoinWithCode offer={offer} onBack={() => { setOffer(null); setCode(""); }} onSkip={onSkip} onJoined={onJoined} />;
  // The field keeps its name ("Promo code") without the visible label. A
  // promo code waives the fee; the team code that joins a team goes on the
  // team step (the server answers 409 team_code when one is typed here).
  const field = <Input className="uppercase tracking-[0.12em]" placeholder="Enter code" value={code} error={!!error} onChange={(e) => { setCode(e.target.value); if (error) setError(""); }} onKeyDown={(e) => { if (e.key === "Enter") apply(); }} aria-label="Promo code" />;
  const message = error && <p className="bm-body-m text-bm-critical">{error}</p>;
  return (
    <>
      <TitleBlock eyebrow="Optional" title={error ? "Have a code?" : "Enter your promo code."} sub="A promo code from your school or coach waives the fee. You never need one to finish. Skip in one tap." />
      {phone ? <>{field}{message}</> : <Field label="Code">{field}{message}</Field>}
      <Note>{error ? "You can skip this and enter a code later at the payment step. Nothing is lost." : "Applying a code now skips checkout later. You can also enter one at the payment step."}</Note>
      <Actions>
        <PrimaryBtn onClick={apply} loading={busy}>{error ? "Try again" : "Apply code"}</PrimaryBtn>
        <SecondaryBtn onClick={onSkip} disabled={busy}>Skip for now</SecondaryBtn>
      </Actions>
    </>
  );
};

// The server's rate card keys (nil_rate_card.compute_nil_rates): events,
// socialPost, reel, story, longVideo, share. Older payloads carried reelStory
// and the other aliases. A category the server holds back arrives as null.
export const rateRowsOf = (rates) => {
  const r = rates || {};
  return [
    ["Events", r.events ?? r.event], ["Social media post", r.socialPost ?? r.post ?? r.social_post],
    ["Reel / TikTok", r.reel ?? r.reelStory ?? r.video], ["Story", r.story ?? r.reelStory],
    ["Long form video", r.longVideo ?? r.long_form ?? r.longForm], ["Repost", r.share ?? r.repost],
  ].filter(([, v]) => v != null);
};

// Phones (1765:1518): square tiles, 20px by 18px padding, 6px between the
// label and the rate.
export const RateCard = ({ rates }) => {
  const phone = usePhoneTheme();
  const rows = rateRowsOf(rates);
  if (!rows.length) return null;
  return (
    <Section label="NILx rate card" right="Market range for your profile">
      <div className="flex flex-col gap-[14px] rounded-[16px] border border-bm-border bg-white p-[18px]">
        <div className="grid grid-cols-2 gap-[10px] sm:grid-cols-3">
          {rows.map(([label, v]) => (
            <div key={label} className={phone ? "flex flex-col gap-[6px] border border-bm-border px-5 py-[18px]" : "flex flex-col gap-1 rounded-[12px] border border-bm-border px-4 py-3"}><span className="bm-overline !text-bm-muted">{label}</span><span className="ob-num-m text-bm-ink">{fmtMoney(v)}</span></div>
          ))}
        </div>
        <p className="bm-body-s text-bm-tertiary">Rates are a starting range. Brands can offer above or below, and you set your own floor in your profile.</p>
      </div>
    </Section>
  );
};

// The hero line under the name on 16. Figma (1576:766) names the team
// ("Bishop Gorman is covering your season"), but a code is not always
// paid for by the athlete's team, so every width names the code that
// waived the fees.
export const promoHeroSub = (state) => `${state?.promoCode || "Your code"} is covering your season`;

// 16 · Code applied, checkout skipped. Phones (1692:1307): the confirm card
// with the 64px check, 20px by 28px padding; the values keep the web's
// ranges (D-o10).
export const PromoAppliedStep = ({ state, promo, onNext }) => {
  const phone = usePhoneTheme();
  const nil = state.metrics?.nilValuation || {};
  return (
    <>
      <div className={phone ? "flex flex-col items-center gap-[14px] rounded-[24px] border border-bm-border bg-white px-5 py-7 text-center shadow-[0px_4px_16px_0px_rgba(0,0,0,0.06)]" : "flex flex-col items-center gap-[14px] rounded-[24px] border border-bm-border bg-white px-6 py-[26px] text-center shadow-[0px_4px_16px_0px_rgba(0,0,0,0.06)]"}>
        <CheckCircle size={phone ? 64 : 56} />
        <h2 className="bm-h-m text-bm-ink">{promo.code} applied</h2>
        <p className="bm-body-m text-bm-muted">No payment needed. Optimized metrics are unlocked{promo.until ? ` until ${new Date(promo.until).toLocaleDateString()}` : ""}.</p>
      </div>
      <Section label="Now included">
        <RowList rows={[
          { title: "CacheValue", sub: "What a brand would pay you", value: fmtRange(nil.cacheValue ?? nil.value ?? nil.cache_value) },
          { title: "CacheNILx Value", sub: nil.nilxValue?.tier || "Your NIL market value", value: fmtRange(nil.nilxValue ?? nil.nilx ?? nil.cache_nilx) },
        ]} />
      </Section>
      <RateCard rates={state.metrics?.nilRates} />
      <Actions><PrimaryBtn onClick={onNext}>See my Optimized metrics</PrimaryBtn></Actions>
    </>
  );
};
